import { ChevronDown } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type {
  CompanionCoverageSummary,
  CompanionMemberCoverages,
} from '@/services/types/coverage-types';

import { CoverageStatusBadge } from '../CoverageStatusBadge';
import { categoryBadgeConfig } from '../shared/constants';

interface PrimaryCoverageCardProps {
  coverage: CompanionCoverageSummary;
  companionMatters: CompanionMemberCoverages[];
  sharedMatterIds: Set<string>;
  onToggleAssociation: (
    coverageId: string,
    targetMatterId: string,
    checked: boolean,
  ) => void;
}

export const PrimaryCoverageCard = ({
  coverage,
  companionMatters,
  sharedMatterIds,
  onToggleAssociation,
}: PrimaryCoverageCardProps) => {
  const selectedCount = sharedMatterIds.size;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-900">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
            {coverage.carrier_name || 'Unknown Carrier'}
          </p>
          {coverage.policy_number && (
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {coverage.policy_number}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Badge
            variant="outline"
            className="text-xs font-medium px-1.5 py-0"
          >
            {(coverage.category && categoryBadgeConfig[coverage.category]?.text) || 'Other'}
          </Badge>
          <CoverageStatusBadge status={coverage.status} />
        </div>
      </div>

      <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-between text-xs font-medium h-7"
            >
              <span>
                Share with
                {selectedCount > 0 ? ` (${selectedCount})` : ''}
              </span>
              <ChevronDown className="h-3 w-3 text-gray-500 dark:text-gray-400" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-2">
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 px-1 pb-1.5">
              Companion matters
            </p>
            {companionMatters.map((matter) => {
              const isChecked = sharedMatterIds.has(matter.matter_id);
              const checkboxId = `share-${coverage.id}-${matter.matter_id}`;
              return (
                <div
                  key={matter.matter_id}
                  className="flex items-center gap-2 px-1 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  role="none"
                  onClick={() =>
                    onToggleAssociation(
                      coverage.id,
                      matter.matter_id,
                      !isChecked,
                    )
                  }
                >
                  <Checkbox
                    id={checkboxId}
                    checked={isChecked}
                    onCheckedChange={(checked) =>
                      onToggleAssociation(
                        coverage.id,
                        matter.matter_id,
                        Boolean(checked),
                      )
                    }
                    onClick={(e) => e.stopPropagation()}
                    className="size-3 rounded-[3px] border-indigo-300 dark:border-indigo-600 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600 data-[state=checked]:text-white"
                  />
                  <label
                    htmlFor={checkboxId}
                    className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {matter.matter_name}
                  </label>
                </div>
              );
            })}
            {companionMatters.length === 0 && (
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 px-1 py-2">
                No companion matters
              </p>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
